// Keep Meta, Google's tags and PostHog in step with the visitor's choice, on load, on every change and on sign in.

import { userBackendClient } from '@repo/api/services/userBackend';
import {
  clearAttribution,
  commitPendingAttribution,
  deleteCookie,
  hasAdvertisingConsent,
  hasAnalyticsConsent,
  hasAnsweredConsent,
  onConsentChange,
  readConsentChoice,
} from '@repo/utils';

import { authStore } from '@/features/auth/store/authStore';
import { hasTokenExpired } from '@/features/auth/utils/hasTokenExpired';
import { updateGoogleConsent } from '../googleConsent';
import { setMetaPixelConsent } from '../metaPixel';
import { setPostHogConsent } from '../postHogConsent';

const META_COOKIES = ['_fbp', '_fbc'];

// the customer (sub) whose records were deleted, so later page loads do not ask again until a yes or
// a sign out
export const RECORDS_DELETED_STORAGE_KEY = 'pf-meta-records-deleted';

let started = false;
// the customer asked for in this page load; another customer signing in on the same tab is asked for too
let requestedFor: string | null = null;

function readDeletedFor(): string | null {
  try {
    return window.localStorage.getItem(RECORDS_DELETED_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDeletedFor(sub: string | null): void {
  try {
    if (sub) {
      window.localStorage.setItem(RECORDS_DELETED_STORAGE_KEY, sub);
    } else {
      window.localStorage.removeItem(RECORDS_DELETED_STORAGE_KEY);
    }
  } catch {
    // blocked storage only means the deletion is asked again on a later load
  }
}

// isAuthenticated() reads the store's current state, so the previous state is checked by its token expiry
function hadValidToken(exp: number | undefined): boolean {
  if (!exp) {
    return false;
  }
  return !hasTokenExpired(exp * 1000);
}

/** A signed in customer who said no: delete their tracking records on our side, once per customer. */
function requestRecordDeletion(): void {
  const { user, isAuthenticated } = authStore.getState();
  if (!isAuthenticated() || !user?.sub || requestedFor === user.sub || readDeletedFor() === user.sub) {
    return;
  }
  const { sub } = user;
  requestedFor = sub;
  userBackendClient({ method: 'delete', url: '/meta_pixel_events' })
    .then(() => {
      // signed out while the request ran: leave no customer id behind
      if (authStore.getState().user?.sub === sub) {
        writeDeletedFor(sub);
      }
    })
    .catch(() => {
      // the API client reports the error; asked again on the next page load, never blocks the app
      requestedFor = null;
    });
}

function apply(advertising: boolean): void {
  try {
    updateGoogleConsent(readConsentChoice());
    setPostHogConsent(hasAnalyticsConsent());
    setMetaPixelConsent(advertising);
    if (advertising) {
      // a later no deletes again
      requestedFor = null;
      writeDeletedFor(null);
      // the campaign seen before the yes reaches the device only now (Legal, 5 October 2026)
      commitPendingAttribution();
      return;
    }
    // an answered "no" removes every trace (FR-4)
    if (hasAnsweredConsent()) {
      clearAttribution();
      META_COOKIES.forEach(deleteCookie);
      requestRecordDeletion();
    }
  } catch {
    // consent must never break the app
  }
}

/** Applies the current choice, then follows changes. Starts once; returns a stop. */
export function startConsentBridge(): () => void {
  if (started) {
    return () => {};
  }
  started = true;
  apply(hasAdvertisingConsent());
  const unsubscribe = onConsentChange(apply);
  // signing in, or in again after the token expired, without a reload: a "no" already recorded still deletes
  const unsubscribeAuth = authStore.subscribe((state, previous) => {
    if (!state.user && previous.user) {
      // signed out: leave no customer id behind on a shared device
      writeDeletedFor(null);
      return;
    }
    if (state.isAuthenticated() && !hadValidToken(previous.user?.exp)) {
      apply(hasAdvertisingConsent());
    }
  });
  return () => {
    unsubscribe();
    unsubscribeAuth();
    started = false;
    requestedFor = null;
  };
}
